import { reporter } from '@/lib/reporter'
import { AlertCircle, ChevronDown, ChevronRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import type { z } from 'zod'

/**
 * Error component displayed when Zod schema validation fails
 */
export function SchemaValidationError({
  toolId,
  error,
  nodeExecution,
}: {
  toolId?: string
  error: z.ZodError
  nodeExecution: NodeExecution
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const firstError = error.errors[0]
  const errorPath = firstError?.path.join('.') || 'unknown'
  const errorMessage = firstError?.message || 'Invalid data structure'

  // Detect if this is likely an unused parallel path (null outputs, node wasn't executed)
  const isUnusedPath =
    (nodeExecution.outputs === null ||
      nodeExecution.outputs === undefined ||
      Object.keys(nodeExecution.outputs || {}).length === 0) &&
    (errorMessage.includes('Expected object, received null') ||
      errorMessage.includes('Invalid input') ||
      nodeExecution.status === 'skipped')

  // Report schema validation errors to Sentry once when component mounts
  // Only report if this is not an unused parallel path
  useEffect(() => {
    if (isUnusedPath) {
      return
    }

    const schemaError = new Error(`Zod Schema Validation Failed: ${toolId || nodeExecution.nodeId}`)

    // Attach context to the error object
    Object.assign(schemaError, {
      component: 'WorkflowToolResults',
      toolId: toolId || nodeExecution.nodeId || 'unknown',
      nodeId: nodeExecution.nodeId,
      errorPath,
      errorMessage,
      zodErrors: error.errors,
      outputSample: JSON.stringify(nodeExecution.outputs).slice(0, 1000), // Limit size
    })

    reporter.capture(schemaError)
  }, [isUnusedPath, toolId, nodeExecution, errorPath, errorMessage, error.errors])

  // Hide completely if it's just an unused parallel path
  if (isUnusedPath) {
    return null
  }

  return (
    <div className="rounded-lg border border-orange-200/50 bg-orange-50/70 text-black shadow-sm">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center gap-3 border-b border-orange-200/50 px-3 py-2 text-left hover:bg-orange-100/50"
      >
        <div className="flex-shrink-0">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-orange-600" />
          ) : (
            <ChevronRight className="h-4 w-4 text-orange-600" />
          )}
        </div>
        <div className="flex-shrink-0">
          <div className="rounded-full bg-orange-100 p-1.5">
            <AlertCircle className="h-4 w-4 text-orange-600" />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-orange-900">Schema Validation Error</div>
          <div className="text-xs text-orange-700">
            {errorMessage.length > 60 ? `${errorMessage.slice(0, 60)}...` : errorMessage}
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="px-3 py-2">
          <div className="mb-2 rounded-lg border border-orange-200 bg-orange-100/50 p-2">
            <div className="mb-1 text-xs font-semibold text-orange-800">DEBUGGING INFO</div>
            <div className="space-y-1 text-xs text-orange-700">
              <div>
                <span className="font-semibold">Tool ID:</span>{' '}
                {toolId || nodeExecution.inputs?.toolId || 'unknown'}
              </div>
              <div>
                <span className="font-semibold">Node ID:</span> {nodeExecution.nodeId}
              </div>
              {nodeExecution.inputs?.toolId && (
                <div>
                  <span className="font-semibold">Tool Name:</span> {nodeExecution.inputs.toolId}
                </div>
              )}
            </div>
          </div>

          <div className="mb-2 rounded-lg border border-orange-200 bg-orange-100/50 p-2">
            <div className="mb-1 text-xs font-semibold text-orange-800">ERROR DETAILS</div>
            <div className="text-xs text-orange-700">
              <span className="font-semibold">Path:</span> {errorPath}
            </div>
            <div className="text-xs text-orange-700">
              <span className="font-semibold">Message:</span> {errorMessage}
            </div>
          </div>

          {error.errors.length > 1 && (
            <details className="mb-2">
              <summary className="cursor-pointer text-xs font-semibold text-orange-800">
                Show all errors ({error.errors.length})
              </summary>
              <div className="mt-1 space-y-1 rounded-lg border border-orange-200 bg-orange-100/50 p-2">
                {error.errors.map((err, index) => (
                  <div key={index} className="text-xs text-orange-700">
                    • {err.path.join('.')}: {err.message}
                  </div>
                ))}
              </div>
            </details>
          )}

          <details className="mb-2">
            <summary className="cursor-pointer text-xs font-semibold text-gray-700">
              Show raw output
            </summary>
            <pre className="mt-1 max-h-64 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-2 text-xs text-gray-700">
              {JSON.stringify(nodeExecution.outputs, null, 2)}
            </pre>
          </details>

          <details>
            <summary className="cursor-pointer text-xs font-semibold text-gray-700">
              Show inputs
            </summary>
            <pre className="mt-1 max-h-64 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-2 text-xs text-gray-700">
              {JSON.stringify(nodeExecution.inputs, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  )
}
